/*
 * Per-provider booking rules for the slot pickers (scheduler New Appointment,
 * the scheduler's appointment card / reschedule, the orders Schedule popup).
 *
 * Only a practice whose profile carries `slotRules` (7825, see
 * config/practice.js) has any; everyone else -- 9928 included -- keeps care's
 * own picker exactly: one patient per slot (a slot with any booking is
 * disabled), the 0-2 / 3-4 / 5+ colours, every day and every type bookable.
 *
 * A provider the rules do not list gets that same care default (capacity 1),
 * NOT "unlimited" as OSA's own build did for an unlisted name.
 *
 * Rules are keyed by providerid (the /providers list's `providerid`):
 *   maxPerSlot    bookings allowed in one slot (default 1)
 *   allowedDays   dayjs .day() values (0 Sun .. 6 Sat) the provider can be
 *                 booked on; absent = every day
 *   typeMatch     only appointment types whose name contains this text
 *                 (case-insensitive); absent = every type
 *   typeLabel     how the Save message names those types ("telemedicine")
 *   dayOverrides  { <day>: { maxPerSlot, typeMatch } } for one weekday
 */
import dayjs from "dayjs";
import { practiceFeatures, practiceProfile } from "../config/practice";

const CARE_SLOT_CAPACITY = 1;

export function providerSlotRule(providerid) {
  if (!practiceFeatures().providerSlotRules) return null;
  const rules = practiceProfile().slotRules;
  if (!rules || providerid === undefined || providerid === null || providerid === "") return null;
  return rules[String(providerid)] || null;
}

function ruleForDay(rule, date) {
  const override = rule.dayOverrides && date ? rule.dayOverrides[dayjs(date).day()] : null;
  return { ...rule, ...(override || {}) };
}

// Bookings one slot can take for this provider on this date.
export function slotCapacity(providerid, date) {
  const rule = providerSlotRule(providerid);
  if (!rule) return CARE_SLOT_CAPACITY;
  const { maxPerSlot } = ruleForDay(rule, date);
  return Number.isInteger(maxPerSlot) && maxPerSlot > 0 ? maxPerSlot : CARE_SLOT_CAPACITY;
}

// A slot button is disabled once it holds `capacity` bookings (care: 1).
export function isSlotFull(slot, providerid, date) {
  return slot?.appointment_count >= slotCapacity(providerid, date);
}

// The slot button's fill. Without a rule: care's own 0-2 / 3-4 / 5+ colours;
// with one: green while there is room, orange on the last place, red when full.
export function slotColorClass(appointmentCount, providerid, date) {
  const rule = providerSlotRule(providerid);
  if (!rule) {
    return appointmentCount <= 2
      ? 'bg-[#DCFCE7]'
      : appointmentCount <= 4
      ? 'bg-[#FFEDD4]'
      : 'bg-[#FFE2E2]';
  }
  const capacity = slotCapacity(providerid, date);
  if (appointmentCount >= capacity) return 'bg-[#FFE2E2]';
  if (appointmentCount === capacity - 1) return 'bg-[#FFEDD4]';
  return 'bg-[#DCFCE7]';
}

// The date can be booked for this provider (allowedDays).
export function isDateBookable(providerid, date) {
  const rule = providerSlotRule(providerid);
  if (!rule?.allowedDays || !date) return true;
  return rule.allowedDays.includes(dayjs(date).day());
}

// The appointment type can be booked with this provider on this date.
export function isTypeAllowed(providerid, appointmentType, date) {
  const rule = providerSlotRule(providerid);
  if (!rule) return true;
  const { typeMatch } = ruleForDay(rule, date);
  if (!typeMatch) return true;
  return String(appointmentType || "").toLowerCase().includes(String(typeMatch).toLowerCase());
}

// Why this booking breaks the provider's rules ("" when it does not, and
// always "" without a rule). Checked on Save / Reschedule after the picker's
// own "select a provider / type / slot" checks.
export function slotRuleError({ providerid, providername, appointmentType, date, slot, slots }) {
  if (!providerSlotRule(providerid)) return "";
  const name = providername || "This provider";
  if (!isDateBookable(providerid, date)) return `${name} can't be booked on ${dayjs(date).format("dddd")}s`;
  if (appointmentType && !isTypeAllowed(providerid, appointmentType, date)) {
    const rule = providerSlotRule(providerid);
    const { typeMatch, typeLabel } = ruleForDay(rule, date);
    const onlyThatDay = rule.typeMatch ? "" : ` on ${dayjs(date).format("dddd")}s`;
    return `${name} only offers ${typeLabel || typeMatch} appointments${onlyThatDay}`;
  }
  const picked = (slots || []).find((s) => s?.starttime === slot);
  if (picked && isSlotFull(picked, providerid, date)) return "This time slot is full. Please select another slot.";
  return "";
}
